import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  resolvePreferredLangOrBrowserDefault,
  savePreferredLang,
} from "@/i18n/preferredLang";
import type {
  LocaleDictionary,
  Namespace,
  SupportedLangs,
  TranslationContextValue,
  TranslationHook,
} from "@/i18n/types";

export type {
  SupportedLangs,
  LocaleDictionary,
  Namespace,
  TranslationContextValue,
  TranslationHook,
} from "@/i18n/types";

type AnyNamespace = Namespace<string, LocaleDictionary>;

const TranslationContext = createContext<TranslationContextValue>({
  lang: "ja",
  setLang: () => {
    /* no-op */
  },
});

export const TranslationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [lang, setLangState] = useState<SupportedLangs>(
    resolvePreferredLangOrBrowserDefault
  );

  const setLang = useCallback((nextLang: SupportedLangs) => {
    savePreferredLang(nextLang);
    setLangState(nextLang);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<TranslationContextValue>(() => {
    return {
      lang,
      setLang,
    };
  }, [lang, setLang]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

/** 名前空間を必要としない言語切り替え UI 向けに、現在の言語と更新関数だけを取り出す。 */
export const useTranslationLang = (): TranslationContextValue =>
  useContext(TranslationContext);

const buildTranslator = (dict: LocaleDictionary): unknown => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(dict)) {
    if (typeof value === "string") {
      result[key] = () => value;
    } else if (typeof value === "function") {
      result[key] = value;
    } else if (typeof value === "object" && value !== null) {
      result[key] = buildTranslator(value as LocaleDictionary);
    } else {
      throw new Error(`Invalid translation value for key "${key}"`);
    }
  }

  return result;
};

const buildTranslationObject = (
  namespaces: readonly AnyNamespace[],
  lang: SupportedLangs
): unknown => {
  const result: Record<string, unknown> = {};

  for (const namespace of namespaces) {
    result[namespace.name] = buildTranslator(namespace.locales[lang]);
  }

  return result;
};

const createTranslation = <const NS extends readonly AnyNamespace[]>(
  ...namespaces: NS
): TranslationHook<NS> => {
  const translationObjectCache: { [key in SupportedLangs]?: unknown } = {};
  const useTranslation = (() => {
    const context = useContext(TranslationContext);

    const t = useMemo(() => {
      if (translationObjectCache[context.lang]) {
        return translationObjectCache[context.lang]!;
      }
      const translationObject = buildTranslationObject(
        namespaces,
        context.lang
      );
      translationObjectCache[context.lang] = translationObject;
      return translationObject;
    }, [context.lang]);

    return {
      ...context,
      t,
    };
  }) as TranslationHook<NS>;

  useTranslation.use = <const More extends readonly AnyNamespace[]>(
    ...more: More
  ) => {
    return createTranslation(...namespaces, ...more);
  };

  return useTranslation;
};

export const translation = createTranslation();
